/**
 * useCloneItems - Shared "clone one item's data onto other items" behavior for
 * the Drug and Procedure output arrays.
 *
 * Flow (see BI-1625 clone feature):
 * 1. Every item shows a duplicate (copy) icon button (idle state).
 * 2. Clicking it enters clone mode with that item as the source; the other
 *    items' buttons become green-tick select toggles.
 * 3. The user selects any number of target items (tick turns green).
 * 4. Clicking the duplicate/confirm icon (with the selected count) on the source
 *    copies the source's section data onto every selected target, preserving
 *    each target's own `code`/`name`.
 *
 * Copy semantics: FULL REPLACE of section data, target keeps its own identity
 * (code/name). Field-level evidence/metadata is stored separately (keyed by
 * array index) and is intentionally NOT cloned — cloned data carries no
 * source-call evidence.
 */

import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { cn } from '@utils/index';
import { Copy, CopyPlus, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

/** Minimal item shape the clone logic needs. Extra keys are preserved verbatim. */
export interface CloneableItem {
  code?: string;
  name?: string;
  [key: string]: unknown;
}

interface CloneState {
  sourceIndex: number;
  selected: Set<number>;
}

export interface UseCloneItemsResult {
  /** True while clone mode is active. */
  active: boolean;
  /** Index of the source item, or null when idle. */
  sourceIndex: number | null;
  /** Whether a given target index is currently selected. */
  isSelected: (index: number) => boolean;
  /** Enter clone mode with `index` as the source. */
  start: (index: number) => void;
  /** Toggle a target index in/out of the selection. */
  toggleTarget: (index: number) => void;
  /** Number of currently selected targets. */
  selectedCount: number;
  /** Exit clone mode without writing. */
  cancel: () => void;
  /** Copy source data onto all selected targets and persist via onChange. */
  confirm: () => void;
}

/**
 * Deep-clone a value. structuredClone is available in all supported browsers;
 * falls back to JSON round-trip for the (test) environments that lack it.
 */
function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

export function useCloneItems(
  items: CloneableItem[],
  onChange?: (data: unknown[]) => void,
): UseCloneItemsResult {
  const [state, setState] = useState<CloneState | null>(null);

  const start = useCallback((index: number) => {
    setState({ sourceIndex: index, selected: new Set<number>() });
  }, []);

  const toggleTarget = useCallback((index: number) => {
    setState(prev => {
      if (!prev || index === prev.sourceIndex) return prev;
      const selected = new Set(prev.selected);
      if (selected.has(index)) selected.delete(index);
      else selected.add(index);
      return { ...prev, selected };
    });
  }, []);

  const cancel = useCallback(() => setState(null), []);

  const confirm = useCallback(() => {
    if (!state || !onChange || state.selected.size === 0) {
      setState(null);
      return;
    }
    const source = items[state.sourceIndex];
    const next = items.map((item, index) => {
      if (!state.selected.has(index)) return item;
      // Full replace of section data; preserve the target's own identity.
      return { ...deepClone(source), code: item.code, name: item.name };
    });
    onChange(next);
    setState(null);
  }, [state, onChange, items]);

  return useMemo(
    () => ({
      active: state !== null,
      sourceIndex: state?.sourceIndex ?? null,
      isSelected: (index: number) => state?.selected.has(index) ?? false,
      start,
      toggleTarget,
      selectedCount: state?.selected.size ?? 0,
      cancel,
      confirm,
    }),
    [state, start, toggleTarget, cancel, confirm],
  );
}

/**
 * CloneControls - the per-item header buttons that drive clone mode. Renders
 * nothing when no write path (`clone` absent) is wired. `stopPropagation` keeps
 * clicks from toggling the surrounding expand/collapse header button.
 */
export const CloneControls: React.FC<{
  index: number;
  clone: UseCloneItemsResult | null;
  /** Noun for labels, e.g. "drug" / "procedure". */
  entityLabel?: string;
  /**
   * What is being copied, for labels and the toast. Defaults to "data" — the Output
   * board's full-item replace. The Call Script passes a narrower phrase because its
   * clone copies only ONE question's answers across instances, not the whole item
   * (BV-1575); saying "data" there would overstate what the button does.
   */
  subject?: string;
}> = ({ index, clone, entityLabel = 'item', subject = 'data' }) => {
  if (!clone) return null;

  const Subject = subject.charAt(0).toUpperCase() + subject.slice(1);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Idle: every item offers to start a clone — "copy plus" (duplicate) icon,
  // distinct from the plain Copy icon used for the confirm action.
  if (!clone.active) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="h-7 w-7 p-0 text-muted-foreground"
        onClick={e => {
          stop(e);
          clone.start(index);
        }}
        title={`Duplicate this ${entityLabel}'s ${subject} onto other ${entityLabel}s`}
      >
        <CopyPlus className="w-4 h-4" />
      </Button>
    );
  }

  // Clone mode, this item is the source: confirm (Copy + count) / cancel (X).
  if (clone.sourceIndex === index) {
    const count = clone.selectedCount;
    return (
      <div className="flex items-center gap-1" onClick={stop}>
        <Button
          type="button"
          variant="default"
          size="xs"
          disabled={count === 0}
          onClick={e => {
            stop(e);
            clone.confirm();
            toast.success(
              count === 1
                ? `${Subject} copied to 1 ${entityLabel.toLowerCase()}`
                : `${Subject} copied to ${count} ${entityLabel.toLowerCase()}s`,
            );
          }}
          title={`Copy this ${entityLabel}'s ${subject} onto ${count} selected ${entityLabel}${
            count === 1 ? '' : 's'
          }`}
        >
          <Copy className="w-3.5 h-3.5 mr-1" />
          Copy ({count})
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="h-7 w-7 p-0 text-muted-foreground"
          onClick={e => {
            stop(e);
            clone.cancel();
          }}
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Clone mode, a potential target: real Checkbox (green when checked).
  const selected = clone.isSelected(index);
  return (
    <span onClick={stop} className="flex items-center pr-1">
      <Checkbox
        checked={selected}
        onCheckedChange={() => clone.toggleTarget(index)}
        onClick={stop}
        aria-label={`Select this ${entityLabel} as a clone target`}
        className={cn(
          'h-5 w-5',
          'data-[state=checked]:bg-positive data-[state=checked]:text-positive-foreground data-[state=checked]:border-positive-border',
        )}
      />
    </span>
  );
};
